import {
  resolveCmsFontWeight,
  type CmsColor,
} from "@/lib/cms/cms-theme.constants";
import type {
  LayoutAlign,
  LayoutDirection,
  LayoutJustify,
  LayoutMaxWidth,
} from "@/lib/cms-puck/layout";
import type {
  ColorRef,
  ThemeColorKey,
  SpacingSide,
  SpacingUnit,
  BlockSpacingBox,
  TextAlign,
  TextTransform,
  BlockTextStyle,
  BackgroundFit,
  BackgroundPosition,
  GradientDirection,
  GradientOverlay,
  BackgroundImage,
  ResponsiveFlowPatch,
  ResponsiveStylePatch,
  BorderLineStyle,
  BlockBorderSide,
  BlockBorder,
  BlockCornerRadius,
  BlockShadow,
  BlockLayoutAlign,
  BlockLayoutStyle,
  BlockEntranceDirection,
  BlockEntranceKind,
  BlockEntranceAnimation,
  BlockEffects,
  BlockVisibility,
  BlockAttributes,
  BlockStyle,
  HorizontalAlign,
} from "./types";
import {
  CMS_FLOW_CLASS,
  CMS_STYLE_BREAKPOINTS,
  DEFAULT_BACKGROUND_IMAGE,
  DEFAULT_GRADIENT_OVERLAY,
  DEFAULT_BLOCK_TEXT_STYLE,
  DEFAULT_BLOCK_RADIUS,
  DEFAULT_BLOCK_LAYOUT,
  DEFAULT_BLOCK_EFFECTS,
  DEFAULT_BLOCK_VISIBILITY,
  DEFAULT_BLOCK_ATTRIBUTES,
  DEFAULT_BLOCK_STYLE,
  cloneResponsivePatch,
} from "./defaults";
import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";

/** Au moins un coin a un rayon défini. */
export function hasBlockRadius(radius: BlockCornerRadius): boolean {
  return (
    radius.tl != null ||
    radius.tr != null ||
    radius.br != null ||
    radius.bl != null
  );
}

/** Au moins un côté a une couleur de bordure définie (Design / apparence). */
export function hasCustomBorderColor(border: BlockBorder): boolean {
  return (
    border.top.color != null ||
    border.right.color != null ||
    border.bottom.color != null ||
    border.left.color != null
  );
}

const SPACING_UNITS: readonly SpacingUnit[] = ["px", "rem", "em", "%"];
const TEXT_ALIGNS: readonly TextAlign[] = ["left", "center", "right", "justify"];
const TEXT_TRANSFORMS: readonly TextTransform[] = [
  "uppercase",
  "lowercase",
  "capitalize",
];
const BACKGROUND_FITS: readonly BackgroundFit[] = [
  "cover",
  "contain",
  "stretch",
  "repeat",
];
const BACKGROUND_POSITIONS: readonly BackgroundPosition[] = [
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
];
const GRADIENT_DIRECTIONS: readonly GradientDirection[] = [
  "to-bottom",
  "to-top",
  "to-right",
  "to-left",
  "to-bottom-right",
  "to-bottom-left",
];

/**
 * Expression CSS de chaque couleur du thème de page. Formules identiques aux
 * classes `cms-page-*` de l'ancien front (`globals.css`) ; les variables sont
 * posées par la coquille de page (front) et par l'aperçu racine (BO).
 */
export const THEME_COLOR_CSS: Record<ThemeColorKey, string> = {
  text: "var(--cms-page-fg, #171717)",
  background: "var(--cms-page-bg, #ffffff)",
  muted: "color-mix(in srgb, var(--cms-page-fg, #171717) 70%, transparent)",
  border: "color-mix(in srgb, var(--cms-page-fg, #171717) 20%, transparent)",
  borderStrong:
    "color-mix(in srgb, var(--cms-page-fg, #171717) 35%, transparent)",
  surface:
    "color-mix(in srgb, var(--cms-page-fg, #171717) 8%, var(--cms-page-bg, #ffffff))",
  link: "var(--cms-page-link-color, var(--cms-page-fg, #171717))",
};

/** Normalise une `ColorRef` stockée ; forme ou clé de thème inconnue → `null`. */
export function normalizeColorRef(value: unknown): ColorRef {
  if (!value || typeof value !== "object") return null;
  const ref = value as {
    source?: unknown;
    id?: unknown;
    hex?: unknown;
    key?: unknown;
  };
  if (ref.source === "palette" && typeof ref.id === "string") {
    return { source: "palette", id: ref.id };
  }
  if (ref.source === "custom" && typeof ref.hex === "string") {
    return { source: "custom", hex: ref.hex };
  }
  if (
    ref.source === "theme" &&
    typeof ref.key === "string" &&
    Object.prototype.hasOwnProperty.call(THEME_COLOR_CSS, ref.key)
  ) {
    return { source: "theme", key: ref.key as ThemeColorKey };
  }
  return null;
}

function normalizeSide(value: unknown): SpacingSide {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeBox(value: unknown): BlockSpacingBox {
  const box = (value ?? {}) as Partial<BlockSpacingBox>;
  return {
    top: normalizeSide(box.top),
    right: normalizeSide(box.right),
    bottom: normalizeSide(box.bottom),
    left: normalizeSide(box.left),
  };
}

/** Boîte desktop : `null`/absent → `0` (champs Design toujours numériques). */
function normalizeSpacingBox(value: unknown): BlockSpacingBox {
  const box = normalizeBox(value);
  return {
    top: box.top ?? 0,
    right: box.right ?? 0,
    bottom: box.bottom ?? 0,
    left: box.left ?? 0,
  };
}

/** Côté éligible aux marges auto (align center/left/right) : non défini ou 0. */
function marginSideAllowsAuto(value: SpacingSide): boolean {
  return value == null || value === 0;
}

function normalizeTextStyle(value: unknown): BlockTextStyle {
  const t = (value ?? {}) as Partial<BlockTextStyle>;
  return {
    fontSize: normalizeSide(t.fontSize),
    align: TEXT_ALIGNS.includes(t.align as TextAlign)
      ? (t.align as TextAlign)
      : null,
    transform: TEXT_TRANSFORMS.includes(t.transform as TextTransform)
      ? (t.transform as TextTransform)
      : null,
    bold: t.bold === true,
    italic: t.italic === true,
    underline: t.underline === true,
    strikethrough: t.strikethrough === true,
    lineHeight: normalizeSide(t.lineHeight),
    letterSpacing: normalizeSide(t.letterSpacing),
  };
}

function normalizeGradientOverlay(value: unknown): GradientOverlay {
  const o = (value ?? {}) as Partial<GradientOverlay>;
  const opacity =
    typeof o.opacity === "number" && Number.isFinite(o.opacity)
      ? Math.min(1, Math.max(0, o.opacity))
      : DEFAULT_GRADIENT_OVERLAY.opacity;
  return {
    enabled: o.enabled === true,
    from: normalizeColorRef(o.from),
    to: normalizeColorRef(o.to),
    direction: GRADIENT_DIRECTIONS.includes(o.direction as GradientDirection)
      ? (o.direction as GradientDirection)
      : "to-bottom",
    opacity,
  };
}

const BORDER_LINE_STYLES: readonly BorderLineStyle[] = [
  "solid",
  "dashed",
  "dotted",
];
const BLOCK_SHADOWS: readonly BlockShadow[] = ["none", "sm", "md", "lg", "xl"];
const LAYOUT_ALIGNS: readonly BlockLayoutAlign[] = ["left", "center", "right"];
const WIDTH_UNITS: ReadonlyArray<BlockLayoutStyle["widthUnit"]> = ["px", "%"];

function normalizeBorderSide(value: unknown): BlockBorderSide {
  const s = (value ?? {}) as Partial<BlockBorderSide>;
  return {
    width: normalizeSide(s.width),
    style: BORDER_LINE_STYLES.includes(s.style as BorderLineStyle)
      ? (s.style as BorderLineStyle)
      : "solid",
    color: normalizeColorRef(s.color),
  };
}

function normalizeBorder(value: unknown): BlockBorder {
  const b = (value ?? {}) as Partial<BlockBorder>;
  return {
    top: normalizeBorderSide(b.top),
    right: normalizeBorderSide(b.right),
    bottom: normalizeBorderSide(b.bottom),
    left: normalizeBorderSide(b.left),
  };
}

function normalizeRadius(value: unknown): BlockCornerRadius {
  const r = (value ?? {}) as Partial<BlockCornerRadius>;
  return {
    tl: normalizeSide(r.tl),
    tr: normalizeSide(r.tr),
    br: normalizeSide(r.br),
    bl: normalizeSide(r.bl),
  };
}

const LAYOUT_CONTENT_MAX_WIDTHS: readonly LayoutMaxWidth[] = [
  "full",
  "1024",
  "1280",
  "1320",
  "1440",
  "1536",
  "prose",
];

function normalizeContentMaxWidth(value: unknown): LayoutMaxWidth | null {
  if (value == null) return null;
  return LAYOUT_CONTENT_MAX_WIDTHS.includes(value as LayoutMaxWidth)
    ? (value as LayoutMaxWidth)
    : null;
}

function normalizeLayout(value: unknown): BlockLayoutStyle {
  const l = (value ?? {}) as Partial<BlockLayoutStyle>;
  return {
    width: normalizeSide(l.width),
    widthUnit: WIDTH_UNITS.includes(l.widthUnit as BlockLayoutStyle["widthUnit"])
      ? (l.widthUnit as BlockLayoutStyle["widthUnit"])
      : "%",
    maxWidth: normalizeSide(l.maxWidth),
    align: LAYOUT_ALIGNS.includes(l.align as BlockLayoutAlign)
      ? (l.align as BlockLayoutAlign)
      : null,
    contentMaxWidth: normalizeContentMaxWidth(l.contentMaxWidth),
    zIndex: normalizeSide(l.zIndex),
  };
}

const ENTRANCE_ANIMATIONS: readonly BlockEntranceAnimation[] = [
  "none",
  "fade-in",
  "slide-up",
  "slide-down",
  "slide-left",
  "slide-right",
];

export function entranceKind(
  entrance: BlockEntranceAnimation,
): BlockEntranceKind {
  if (entrance === "fade-in") return "fade-in";
  if (entrance.startsWith("slide-")) return "slide";
  return "none";
}

export function entranceDirection(
  entrance: BlockEntranceAnimation,
): BlockEntranceDirection {
  if (entrance === "slide-down") return "down";
  if (entrance === "slide-left") return "left";
  if (entrance === "slide-right") return "right";
  return "up";
}

export function combineEntrance(
  kind: BlockEntranceKind,
  direction: BlockEntranceDirection = "up",
): BlockEntranceAnimation {
  if (kind === "fade-in") return "fade-in";
  if (kind === "slide") return `slide-${direction}`;
  return "none";
}

function normalizeEffects(value: unknown): BlockEffects {
  const e = (value ?? {}) as Partial<BlockEffects>;
  const stickyTopRaw = e.stickyTop;
  const stickyTop =
    stickyTopRaw == null
      ? 0
      : Number.isFinite(Number(stickyTopRaw))
        ? Math.max(0, Math.round(Number(stickyTopRaw)))
        : 0;
  const entrance = ENTRANCE_ANIMATIONS.includes(
    e.entrance as BlockEntranceAnimation,
  )
    ? (e.entrance as BlockEntranceAnimation)
    : "none";
  return {
    sticky: e.sticky === true,
    stickyTop,
    entrance,
  };
}

/** Classe CSS d'animation d'entrée (à poser sur le wrapper du bloc). */
export function blockEntranceClass(entrance: BlockEntranceAnimation): string | null {
  if (entrance === "fade-in") return "cms-blk-entrance-fade";
  if (entrance.startsWith("slide-")) {
    return `cms-blk-entrance-${entrance}`;
  }
  return null;
}

/** Keyframes + classes d'animation d'entrée (injectés une fois par page). */
export const BLOCK_ENTRANCE_CSS = `
@keyframes cms-blk-fade-in{from{opacity:0}to{opacity:1}}
@keyframes cms-blk-slide-up{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
@keyframes cms-blk-slide-down{from{opacity:0;transform:translateY(-16px)}to{opacity:1;transform:translateY(0)}}
@keyframes cms-blk-slide-left{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
@keyframes cms-blk-slide-right{from{opacity:0;transform:translateX(-16px)}to{opacity:1;transform:translateX(0)}}
.cms-blk-entrance-fade{animation:cms-blk-fade-in .55s ease-out both}
.cms-blk-entrance-slide-up{animation:cms-blk-slide-up .55s ease-out both}
.cms-blk-entrance-slide-down{animation:cms-blk-slide-down .55s ease-out both}
.cms-blk-entrance-slide-left{animation:cms-blk-slide-left .55s ease-out both}
.cms-blk-entrance-slide-right{animation:cms-blk-slide-right .55s ease-out both}
.cms-blk-entrance-slide{animation:cms-blk-slide-up .55s ease-out both}
`;

/**
 * Largeur max effective du contenu interne : `style.layout.contentMaxWidth` prime
 * (`"full"` = pleine largeur explicite, sans repli) ; `legacyFallback` (ex.
 * `Container.maxWidth` historique) si `contentMaxWidth` est `null`.
 */
export function resolveBlockContentMaxWidth(
  style: BlockStyle | undefined,
  legacyFallback?: LayoutMaxWidth | null,
): LayoutMaxWidth | null {
  const fromStyle = normalizeBlockStyle(style).layout.contentMaxWidth;
  if (fromStyle != null) {
    return fromStyle === "full" ? null : fromStyle;
  }
  if (legacyFallback && legacyFallback !== "full") return legacyFallback;
  return null;
}

function normalizeVisibility(value: unknown): BlockVisibility {
  const v = (value ?? {}) as Partial<BlockVisibility>;
  return {
    hideDesktop: v.hideDesktop === true,
    hideTablet: v.hideTablet === true,
    hideMobile: v.hideMobile === true,
  };
}

function normalizeAttributes(value: unknown): BlockAttributes {
  const a = (value ?? {}) as Partial<BlockAttributes>;
  return {
    cssId:
      typeof a.cssId === "string" && a.cssId.trim() ? a.cssId.trim() : null,
    cssClass:
      typeof a.cssClass === "string" && a.cssClass.trim()
        ? a.cssClass.trim()
        : null,
  };
}

const FLOW_DIRECTIONS: readonly LayoutDirection[] = ["row", "column"];
const FLOW_ALIGNS: readonly LayoutAlign[] = [
  "start",
  "center",
  "end",
  "stretch",
];
const FLOW_JUSTIFIES: readonly LayoutJustify[] = [
  "start",
  "center",
  "end",
  "between",
];

/** Valeur d'une liste fermée, sinon `null`. */
function oneOf<T extends string>(list: readonly T[], value: unknown): T | null {
  return list.includes(value as T) ? (value as T) : null;
}

/** Nombre de pistes d'une grille : entier de 1 à `MAX_CONTENT_COLUMNS`. */
function isTrackCount(n: number | null): n is number {
  return n != null && Number.isInteger(n) && n >= 1 && n <= MAX_CONTENT_COLUMNS;
}

function normalizeResponsiveFlow(value: unknown): ResponsiveFlowPatch {
  const f = (value ?? {}) as Partial<ResponsiveFlowPatch>;
  const gapPx = (v: unknown) => {
    const n = normalizeSide(v);
    return n != null && n >= 0 ? n : null;
  };
  const columns = normalizeSide(f.columns);
  return {
    direction: oneOf(FLOW_DIRECTIONS, f.direction),
    alignItems: oneOf(FLOW_ALIGNS, f.alignItems),
    justify: oneOf(FLOW_JUSTIFIES, f.justify),
    gap: gapPx(f.gap),
    columnGap: gapPx(f.columnGap),
    rowGap: gapPx(f.rowGap),
    columns: isTrackCount(columns) ? columns : null,
  };
}

function normalizeResponsivePatch(value: unknown): ResponsiveStylePatch {
  const p = (value ?? {}) as Partial<ResponsiveStylePatch>;
  return {
    margin: normalizeBox(p.margin),
    padding: normalizeBox(p.padding),
    width: normalizeSide(p.width),
    fontSize: normalizeSide(p.fontSize),
    letterSpacing: normalizeSide(p.letterSpacing),
    lineHeight: normalizeSide(p.lineHeight),
    textAlign: oneOf(TEXT_ALIGNS, p.textAlign),
    flow: normalizeResponsiveFlow(p.flow),
  };
}

function normalizeBackgroundImage(value: unknown): BackgroundImage {
  const b = (value ?? {}) as Partial<BackgroundImage>;
  return {
    attachmentId:
      typeof b.attachmentId === "string" && b.attachmentId.trim()
        ? b.attachmentId
        : null,
    fit: BACKGROUND_FITS.includes(b.fit as BackgroundFit)
      ? (b.fit as BackgroundFit)
      : "cover",
    position: BACKGROUND_POSITIONS.includes(b.position as BackgroundPosition)
      ? (b.position as BackgroundPosition)
      : "center",
    overlay: normalizeGradientOverlay(b.overlay),
  };
}

/**
 * Normalise une valeur de style partielle/inconnue vers un `BlockStyle` complet.
 * Tolère `undefined`/partiel → DEFAULT. Marges/paddings desktop absents ou
 * `null` → `0` (champs Design toujours renseignés). CRITICAL : pages existantes
 * sans erreur.
 */
export function normalizeBlockStyle(
  value: Partial<BlockStyle> | null | undefined,
): BlockStyle {
  if (!value || typeof value !== "object") {
    return {
      design: {
        ...DEFAULT_BLOCK_STYLE.design,
        backgroundImage: {
          ...DEFAULT_BACKGROUND_IMAGE,
          overlay: { ...DEFAULT_GRADIENT_OVERLAY },
        },
        typography: { ...DEFAULT_BLOCK_STYLE.design.typography },
        text: { ...DEFAULT_BLOCK_TEXT_STYLE },
        border: normalizeBorder(undefined),
        radius: { ...DEFAULT_BLOCK_RADIUS },
        shadow: "none",
      },
      spacing: {
        margin: { ...DEFAULT_BLOCK_STYLE.spacing.margin },
        padding: { ...DEFAULT_BLOCK_STYLE.spacing.padding },
        unit: DEFAULT_BLOCK_STYLE.spacing.unit,
      },
      layout: { ...DEFAULT_BLOCK_LAYOUT },
      effects: { ...DEFAULT_BLOCK_EFFECTS },
      visibility: { ...DEFAULT_BLOCK_VISIBILITY },
      attributes: { ...DEFAULT_BLOCK_ATTRIBUTES },
      responsive: {
        tablet: cloneResponsivePatch(),
        mobile: cloneResponsivePatch(),
      },
    };
  }
  const design = value.design ?? {};
  const spacing = value.spacing ?? {};
  const responsive = (value.responsive ?? {}) as Partial<
    BlockStyle["responsive"]
  >;
  const typography = (design as BlockStyle["design"]).typography ?? {};
  const unit = (spacing as BlockStyle["spacing"]).unit;
  const shadow = (design as BlockStyle["design"]).shadow;
  return {
    design: {
      background: normalizeColorRef((design as BlockStyle["design"]).background),
      backgroundImage: normalizeBackgroundImage(
        (design as BlockStyle["design"]).backgroundImage,
      ),
      textColor: normalizeColorRef((design as BlockStyle["design"]).textColor),
      typography: {
        fontFamily:
          typeof typography.fontFamily === "string" ? typography.fontFamily : null,
        fontVariant:
          typeof typography.fontVariant === "string" ? typography.fontVariant : null,
      },
      text: normalizeTextStyle((design as BlockStyle["design"]).text),
      border: normalizeBorder((design as BlockStyle["design"]).border),
      radius: normalizeRadius((design as BlockStyle["design"]).radius),
      shadow: BLOCK_SHADOWS.includes(shadow as BlockShadow)
        ? (shadow as BlockShadow)
        : "none",
    },
    spacing: {
      margin: normalizeSpacingBox((spacing as BlockStyle["spacing"]).margin),
      padding: normalizeSpacingBox((spacing as BlockStyle["spacing"]).padding),
      unit: SPACING_UNITS.includes(unit as SpacingUnit)
        ? (unit as SpacingUnit)
        : "px",
    },
    layout: normalizeLayout(value.layout),
    effects: normalizeEffects(value.effects),
    visibility: normalizeVisibility(value.visibility),
    attributes: normalizeAttributes(value.attributes),
    responsive: {
      tablet: normalizeResponsivePatch(responsive.tablet),
      mobile: normalizeResponsivePatch(responsive.mobile),
    },
  };
}

/**
 * Alignement horizontal effectif : `style.design.text.align` (Design) prime ;
 * sinon repli sur la prop structurale legacy (`align` du bloc).
 */
export function resolveBlockTextAlign(
  style: BlockStyle | undefined,
  fallback: HorizontalAlign = "left",
): HorizontalAlign {
  if (!style) return fallback;
  const a = normalizeBlockStyle(style).design.text.align;
  if (a === "left" || a === "center" || a === "right") return a;
  return fallback;
}

/**
 * Variante de `resolveBlockTextAlign` pour le texte courant (RichText) : garde
 * `justify`, que les blocs à mise en page flex ne savent pas représenter.
 */
export function resolveBlockProseAlign(
  style: BlockStyle | undefined,
  fallback: HorizontalAlign = "left",
): TextAlign {
  if (!style) return fallback;
  return normalizeBlockStyle(style).design.text.align ?? fallback;
}

/**
 * Résout une `ColorRef` en couleur CSS pour le rendu inline.
 *  - `null`/undefined → `undefined` (hérite → pas de style inline, laisse cascader) ;
 *  - `custom`         → hex figé ;
 *  - `palette`        → hex courant dans la palette ; id introuvable (couleur
 *    supprimée) → `undefined` (hérite, jamais de crash ni de blanc forcé) ;
 *  - `theme`          → expression CSS (`var(...)` / `color-mix(...)`), PAS un
 *    hex : ne pas la passer à un calcul qui attend un hex.
 */
export function resolveColorRef(
  palette: CmsColor[],
  ref: ColorRef | undefined | null,
): string | undefined {
  if (!ref) return undefined;
  if (ref.source === "custom") return ref.hex || undefined;
  if (ref.source === "theme") return THEME_COLOR_CSS[ref.key];
  // source === 'palette'
  const found = palette.find((c) => c.id === ref.id);
  return found?.hex;
}

const BOX_SIDES: Array<[keyof BlockSpacingBox, string, string]> = [
  ["top", "Top", "top"],
  ["right", "Right", "right"],
  ["bottom", "Bottom", "bottom"],
  ["left", "Left", "left"],
];

function emitBox(
  out: Record<string, string | number>,
  prefix: "margin" | "padding",
  box: BlockSpacingBox,
  unit: SpacingUnit,
  /** Côtés à NE PAS émettre en inline (gérés par les règles responsives). */
  skip?: (side: keyof BlockSpacingBox) => boolean,
): void {
  for (const [side, suffix] of BOX_SIDES) {
    if (skip?.(side)) continue;
    const n = box[side];
    if (n != null) out[`${prefix}${suffix}`] = `${n}${unit}`;
  }
}

/* --------------------- Prédicats de surcharge responsive ------------------ */

/** Un côté margin/padding a-t-il une surcharge tablette OU mobile ? */
function sideHasResponsive(
  style: BlockStyle,
  group: "margin" | "padding",
  side: keyof BlockSpacingBox,
): boolean {
  return (
    style.responsive.tablet[group][side] != null ||
    style.responsive.mobile[group][side] != null
  );
}

function scalarHasResponsive(
  style: BlockStyle,
  key: "width" | "fontSize" | "letterSpacing" | "lineHeight",
): boolean {
  return (
    style.responsive.tablet[key] != null || style.responsive.mobile[key] != null
  );
}

/**
 * Alignement surchargé par appareil ? Seulement si l'alignement desktop
 * (`design.text.align`) est défini : la surcharge le remplace, sans lui le
 * desktop garde l'alignement propre au bloc (prop de contenu `align`).
 */
function textAlignHasResponsive(style: BlockStyle): boolean {
  return (
    style.design.text.align != null &&
    (style.responsive.tablet.textAlign != null ||
      style.responsive.mobile.textAlign != null)
  );
}

/**
 * Style rendu d'un bloc Colonnes, dérivé du style stocké :
 *  - une surcharge de colonnes au-delà du nombre d'emplacements est restée
 *    d'un réglage Bureau plus large : elle est ignorée (le Bureau s'applique,
 *    largeurs personnalisées comprises) ;
 *  - « Empiler sur mobile » : la règle tablette des colonnes (`!important`,
 *    plus spécifique) reste active sous 768 px et écraserait l'empilement ;
 *    sans choix mobile explicite, le mobile repasse à 1 colonne.
 */
export function resolveColumnsBlockStyle(
  style: BlockStyle | undefined,
  block: { slots: number; stackOnMobile: boolean },
): BlockStyle | undefined {
  if (!style) return style;
  const normalized = normalizeBlockStyle(style);
  const { tablet, mobile } = normalized.responsive;
  const valid = (columns: number | null) =>
    columns != null && columns <= block.slots ? columns : null;
  const tabletColumns = valid(tablet.flow.columns);
  const ownMobile = valid(mobile.flow.columns);
  const mobileColumns =
    block.stackOnMobile && tabletColumns != null && ownMobile == null
      ? 1
      : ownMobile;
  if (
    tabletColumns === tablet.flow.columns &&
    mobileColumns === mobile.flow.columns
  ) {
    return style;
  }
  return {
    ...normalized,
    responsive: {
      tablet: { ...tablet, flow: { ...tablet.flow, columns: tabletColumns } },
      mobile: { ...mobile, flow: { ...mobile.flow, columns: mobileColumns } },
    },
  };
}

/**
 * Style sans alignement de texte, pour un bloc centré par construction
 * (Bandeau) : une valeur stockée avant que l'interface ne la masque ne doit
 * plus s'appliquer. Sans alignement Bureau, les surcharges par appareil sont
 * ignorées elles aussi (cf. textAlignHasResponsive).
 */
export function withoutTextAlign(
  style: BlockStyle | undefined,
): BlockStyle | undefined {
  if (!style) return style;
  const normalized = normalizeBlockStyle(style);
  if (normalized.design.text.align == null) return style;
  return {
    ...normalized,
    design: {
      ...normalized.design,
      text: { ...normalized.design.text, align: null },
    },
  };
}

/** Une surcharge de mise en page (`responsive.*.flow`) est-elle posée ? */
function flowHasOverride(flow: ResponsiveFlowPatch): boolean {
  return Object.values(flow).some((v) => v != null);
}

/**
 * Le bloc a-t-il besoin de règles CSS générées (`<style>` scopé par classe) en
 * plus du style inline ? Vrai dès qu'une surcharge tablette/mobile OU une règle
 * de visibilité par appareil existe. Sert au wrapper pour ne rien injecter sur
 * les pages existantes (aucune règle → markup strictement identique).
 */
export function blockStyleNeedsCssRules(style: BlockStyle): boolean {
  const v = style.visibility;
  if (v.hideDesktop || v.hideTablet || v.hideMobile) return true;
  if (textAlignHasResponsive(style)) return true;
  if (
    flowHasOverride(style.responsive.tablet.flow) ||
    flowHasOverride(style.responsive.mobile.flow)
  ) {
    return true;
  }
  for (const patch of [style.responsive.tablet, style.responsive.mobile]) {
    if (
      patch.width != null ||
      patch.fontSize != null ||
      patch.letterSpacing != null ||
      patch.lineHeight != null
    ) {
      return true;
    }
    for (const [side] of BOX_SIDES) {
      if (patch.margin[side] != null || patch.padding[side] != null) return true;
    }
  }
  return false;
}

/**
 * Résout un `BlockStyle` en propriétés CSS inline pour le wrapper du bloc.
 * N'émet QUE les propriétés DÉFINIES (null/undefined omis) afin de préserver
 * l'héritage de la page/thème (ne JAMAIS poser color:'#FFFFFF' par défaut).
 *
 * Le typage de retour est volontairement `Record<string, string | number>`
 * (compatible `React.CSSProperties` côté wrapper) pour rester sans dépendance
 * React dans ce module neutre. Valeurs dynamiques → style inline (les classes
 * Tailwind ne sont pas JIT-safe pour des valeurs arbitraires).
 */
/**
 * Résout le sous-ensemble TEXTE d'un `BlockStyle` (couleur, police, graisse,
 * taille, alignement, casse, italique, décoration) en CSS inline. N'émet QUE les
 * propriétés DÉFINIES (héritage préservé).
 *
 * Utilisé tel quel SUR L'ÉLÉMENT de texte (ex. le `<h1>` du bloc Titre, qui porte
 * des classes Tailwind `text-4xl`/`text-left` qu'un style sur un wrapper ne
 * pourrait pas surcharger). Graisse : inline si Design (variante/gras), sinon
 * héritage page — pas de `font-semibold` hardcodé. `resolveBlockStyleCss` (wrapper)
 * le réutilise.
 */
export function resolveBlockTextCss(
  palette: CmsColor[],
  style: BlockStyle,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};

  const color = resolveColorRef(palette, style.design.textColor);
  if (color) out.color = color;

  const { fontFamily, fontVariant } = style.design.typography;
  if (fontFamily) {
    out.fontFamily = `'${fontFamily}', sans-serif`;
  }
  // Graisse : émise dès qu'une FAMILLE ou une VARIANTE est choisie. La variante
  // SEULE (sans famille) fixe un poids (ex. « regular »=400, « medium »=500) SANS
  // forcer la police — la famille reste héritée de l'apparence de page. Permet aux
  // modèles de reproduire la graisse de l'ancien front sans figer la police.
  if (fontFamily || fontVariant) {
    out.fontWeight = resolveCmsFontWeight(fontFamily, fontVariant);
  }

  const text = style.design.text;
  // Taille/lettrage : quand une surcharge tablette/mobile existe, la valeur va
  // dans les règles générées du WRAPPER (base + @media, cf. buildBlockCssRules)
  // et l'élément de texte émet `inherit` EN INLINE → il bat ses classes Tailwind
  // (text-3xl…) tout en SUIVANT la cascade du wrapper à chaque breakpoint.
  if (scalarHasResponsive(style, "fontSize")) {
    out.fontSize = "inherit";
  } else if (text.fontSize != null) {
    out.fontSize = `${text.fontSize}px`;
  }
  // Gras : bascule explicite (onglet Design) → prime sur la graisse de la variante.
  if (text.bold) out.fontWeight = 700;
  // Alignement : même principe que la taille (règles du wrapper + `inherit`).
  if (textAlignHasResponsive(style)) out.textAlign = "inherit";
  else if (text.align) out.textAlign = text.align;
  if (text.transform) out.textTransform = text.transform;
  if (text.italic) out.fontStyle = "italic";
  if (scalarHasResponsive(style, "letterSpacing")) {
    out.letterSpacing = "inherit";
  } else if (text.letterSpacing != null) {
    out.letterSpacing = `${text.letterSpacing}px`;
  }
  // Interligne : via la variable CSS `--cms-line-height` (et NON `line-height`
  // direct) car la règle globale `* { line-height: var(--cms-line-height, 110%) }`
  // la lit À CHAQUE élément — c'est le seul moyen pour que l'interligne atteigne
  // les enfants d'un bloc RichText (sinon la règle `*` écrase l'héritage).
  // Cas responsive : la variable est portée par les règles générées du wrapper
  // (base + @media) — RIEN en inline, sinon l'inline battrait les règles.
  if (
    !scalarHasResponsive(style, "lineHeight") &&
    text.lineHeight != null
  ) {
    out["--cms-line-height"] = String(text.lineHeight);
  }
  const decoration: string[] = [];
  if (text.underline) decoration.push("underline");
  if (text.strikethrough) decoration.push("line-through");
  if (decoration.length) out.textDecorationLine = decoration.join(" ");

  return out;
}

/**
 * Applique une opacité à une couleur résolue : hex (#rgb / #rrggbb, `#`
 * facultatif comme pour une couleur libre saisie) → `rgba(...)` ; couleur du
 * thème (`var(...)` / `color-mix(...)`) → `color-mix(...)` ; sinon `undefined`.
 */
function colorWithOpacity(color: string, opacity: number): string | undefined {
  const a = Math.min(1, Math.max(0, opacity));
  const value = color.trim();
  let h = value.replace(/^#/, "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 6 && !/[^0-9a-fA-F]/.test(h)) {
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  if (/^(var|color-mix)\(/.test(value)) {
    return `color-mix(in srgb, ${value} ${Math.round(a * 100)}%, transparent)`;
  }
  return undefined;
}

const CSS_GRADIENT_DIRECTION: Record<GradientDirection, string> = {
  "to-bottom": "to bottom",
  "to-top": "to top",
  "to-right": "to right",
  "to-left": "to left",
  "to-bottom-right": "to bottom right",
  "to-bottom-left": "to bottom left",
};

const CSS_BG_POSITION: Record<BackgroundPosition, string> = {
  center: "center",
  top: "top",
  bottom: "bottom",
  left: "left",
  right: "right",
  "top-left": "left top",
  "top-right": "right top",
  "bottom-left": "left bottom",
  "bottom-right": "right bottom",
};

/**
 * Construit la valeur d'un dégradé overlay (`linear-gradient(...)`) ou `undefined`
 * s'il est désactivé / sans couleur. `to` absent ⇒ on duplique `from` (teinte unie).
 */
export function resolveBackgroundOverlayGradient(
  palette: CmsColor[],
  overlay: GradientOverlay,
): string | undefined {
  if (!overlay.enabled) return undefined;
  const fromColor = resolveColorRef(palette, overlay.from);
  const toColor = resolveColorRef(palette, overlay.to) ?? fromColor;
  if (!fromColor) return undefined;
  const from = colorWithOpacity(fromColor, overlay.opacity);
  const to = colorWithOpacity(toColor ?? fromColor, overlay.opacity);
  if (!from || !to) return undefined;
  return `linear-gradient(${CSS_GRADIENT_DIRECTION[overlay.direction]}, ${from}, ${to})`;
}

/**
 * Résout un `BlockStyle` en propriétés CSS inline pour le wrapper du bloc.
 * N'émet QUE les propriétés DÉFINIES (null/undefined omis) afin de préserver
 * l'héritage de la page/thème (ne JAMAIS poser color:'#FFFFFF' par défaut).
 *
 * `opts.backgroundImageUrl` : URL de l'image de fond déjà résolue par l'appelant
 * (refs front / react-query BO ; ce module reste PUR, sans fetch). L'overlay
 * (dégradé) est empilé AU-DESSUS de l'image (`linear-gradient(...), url(...)`).
 * La couleur de fond (`design.background`) reste appliquée SOUS l'image.
 *
 * Le typage de retour est `Record<string, string | number>` (compatible
 * `React.CSSProperties`) pour rester sans dépendance React dans ce module neutre.
 */
/** box-shadow des presets d'ombre (équivalents Tailwind shadow-*). */
const BLOCK_SHADOW_CSS: Record<Exclude<BlockShadow, "none">, string> = {
  sm: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
  md: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  lg: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
  xl: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
};

export function resolveBlockStyleCss(
  palette: CmsColor[],
  style: BlockStyle,
  opts?: {
    backgroundImageUrl?: string;
    /** Vidéo/embed rendus en calque DOM — pas de CSS background-image. */
    renderBackgroundAsMedia?: boolean;
  },
): Record<string, string | number> {
  const out: Record<string, string | number> = resolveBlockTextCss(
    palette,
    style,
  );
  // Le `inherit` de fontSize/letterSpacing (cas responsive) est destiné aux
  // ÉLÉMENTS de texte internes (useBlockTextCss) ; sur le WRAPPER il battrait la
  // règle de classe générée (inline > classe) → on retire ces clés, les règles
  // base/@media de buildBlockCssRules portent la valeur.
  if (scalarHasResponsive(style, "fontSize")) delete out.fontSize;
  if (scalarHasResponsive(style, "letterSpacing")) delete out.letterSpacing;
  if (textAlignHasResponsive(style)) delete out.textAlign;

  const background = resolveColorRef(palette, style.design.background);
  if (background) out.backgroundColor = background;

  // Image de fond (+ dégradé overlay). L'URL vient de l'appelant.
  const bgImg = style.design.backgroundImage;
  const url = opts?.backgroundImageUrl;
  if (!opts?.renderBackgroundAsMedia) {
    const overlay = resolveBackgroundOverlayGradient(palette, bgImg.overlay);
    const layers: string[] = [];
    if (overlay) layers.push(overlay);
    if (url) layers.push(`url('${url}')`);
    if (layers.length) {
    out.backgroundImage = layers.join(", ");
    if (url) {
      out.backgroundSize =
        bgImg.fit === "cover"
          ? "cover"
          : bgImg.fit === "contain"
            ? "contain"
            : bgImg.fit === "stretch"
              ? "100% 100%"
              : "auto";
      out.backgroundPosition = CSS_BG_POSITION[bgImg.position];
      out.backgroundRepeat = bgImg.fit === "repeat" ? "repeat" : "no-repeat";
    }
    }
  }

  // Bordures + rayons + ombre (desktop, non responsives en v1).
  for (const [side, suffix] of BOX_SIDES) {
    const b = style.design.border[side];
    const color = resolveColorRef(palette, b.color);
    if (b.width != null) {
      if (b.width === 0) {
        out[`border${suffix}Width`] = "0";
        out[`border${suffix}Style`] = "none";
      } else {
        out[`border${suffix}Width`] = `${b.width}px`;
        out[`border${suffix}Style`] = b.style;
        out[`border${suffix}Color`] = color ?? "currentColor";
      }
    } else if (color) {
      // Couleur sans largeur explicite → surcharge la bordure preset (ex. outline).
      out[`border${suffix}Color`] = color;
    }
  }
  const radius = style.design.radius;
  if (radius.tl != null) out.borderTopLeftRadius = `${radius.tl}px`;
  if (radius.tr != null) out.borderTopRightRadius = `${radius.tr}px`;
  if (radius.br != null) out.borderBottomRightRadius = `${radius.br}px`;
  if (radius.bl != null) out.borderBottomLeftRadius = `${radius.bl}px`;
  if (hasBlockRadius(radius)) out.overflow = "hidden";
  if (style.design.shadow !== "none") {
    out.boxShadow = BLOCK_SHADOW_CSS[style.design.shadow];
  }

  // Mise en page : largeur (responsive → règles générées), largeur max,
  // alignement du bloc (marges auto sur les côtés NON définis par l'utilisateur),
  // z-index (nécessite un positionnement → position:relative).
  const layout = style.layout;
  if (!scalarHasResponsive(style, "width") && layout.width != null) {
    out.width = `${layout.width}${layout.widthUnit}`;
  }
  if (layout.maxWidth != null) out.maxWidth = `${layout.maxWidth}px`;
  if (layout.zIndex != null) {
    out.zIndex = layout.zIndex;
    out.position = "relative";
  }
  const effects = normalizeEffects(style.effects);
  if (effects.sticky) {
    out.position = "sticky";
    out.top = `${effects.stickyTop ?? 0}px`;
    if (out.zIndex == null) out.zIndex = 10;
  }

  emitBox(out, "margin", style.spacing.margin, style.spacing.unit, (side) =>
    sideHasResponsive(style, "margin", side),
  );
  emitBox(out, "padding", style.spacing.padding, style.spacing.unit, (side) =>
    sideHasResponsive(style, "padding", side),
  );

  // Alignement horizontal : marges auto APRÈS emitBox, si côté à 0/null
  // (défaut Design = 0 ne doit pas bloquer le centrage).
  if (layout.align) {
    const autoLeft = layout.align === "center" || layout.align === "right";
    const autoRight = layout.align === "center" || layout.align === "left";
    if (
      autoLeft &&
      marginSideAllowsAuto(style.spacing.margin.left) &&
      !sideHasResponsive(style, "margin", "left")
    ) {
      out.marginLeft = "auto";
    }
    if (
      autoRight &&
      marginSideAllowsAuto(style.spacing.margin.right) &&
      !sideHasResponsive(style, "margin", "right")
    ) {
      out.marginRight = "auto";
    }
  }

  return out;
}

/* --------------------- Règles CSS générées (responsive) ------------------- */

function boxDecls(
  decls: string[],
  prefix: "margin" | "padding",
  box: BlockSpacingBox,
  unit: SpacingUnit,
  /** N'émettre QUE les côtés surchargés quelque part (règle de base). */
  onlySides?: (side: keyof BlockSpacingBox) => boolean,
): void {
  for (const [side, , cssSide] of BOX_SIDES) {
    if (onlySides && !onlySides(side)) continue;
    const n = box[side];
    if (n != null) decls.push(`${prefix}-${cssSide}:${n}${unit}`);
  }
}

function patchDecls(
  style: BlockStyle,
  patch: ResponsiveStylePatch,
): string[] {
  const decls: string[] = [];
  const unit = style.spacing.unit;
  boxDecls(decls, "margin", patch.margin, unit);
  boxDecls(decls, "padding", patch.padding, unit);
  if (patch.width != null) {
    decls.push(`width:${patch.width}${style.layout.widthUnit}`);
  }
  if (patch.fontSize != null) decls.push(`font-size:${patch.fontSize}px`);
  if (patch.letterSpacing != null) {
    decls.push(`letter-spacing:${patch.letterSpacing}px`);
  }
  if (patch.lineHeight != null) {
    decls.push(`--cms-line-height:${patch.lineHeight}`);
  }
  if (patch.textAlign != null && textAlignHasResponsive(style)) {
    decls.push(`text-align:${patch.textAlign}`);
  }
  return decls;
}

const FLOW_ALIGN_CSS: Record<LayoutAlign, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
};

const FLOW_JUSTIFY_CSS: Record<LayoutJustify, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
};

/**
 * Règles de mise en page d'un appareil, sur l'élément flex / grid du bloc
 * (`CMS_FLOW_CLASS`) — le premier sous le wrapper, pas ceux des conteneurs
 * imbriqués. `!important` : gap, colonnes et alignement des cellules sont posés
 * en inline ou par des règles scopées des blocs (cf. ColumnsGrid / GridContainer)
 * que la surcharge doit battre. L'alignement vise aussi les enfants
 * (`align-self`), y compris à travers le pont `display: contents` de la Grille.
 */
function flowRules(selector: string, flow: ResponsiveFlowPatch): string[] {
  const target = `${selector} .${CMS_FLOW_CLASS}:not(${selector} .${CMS_FLOW_CLASS} .${CMS_FLOW_CLASS})`;
  const decls: string[] = [];
  if (flow.direction) decls.push(`flex-direction:${flow.direction}`);
  if (flow.alignItems) decls.push(`align-items:${FLOW_ALIGN_CSS[flow.alignItems]}`);
  if (flow.justify) {
    decls.push(`justify-content:${FLOW_JUSTIFY_CSS[flow.justify]}`);
  }
  if (flow.gap != null) decls.push(`gap:${flow.gap}px`);
  if (flow.columnGap != null) decls.push(`column-gap:${flow.columnGap}px`);
  if (flow.rowGap != null) decls.push(`row-gap:${flow.rowGap}px`);
  if (flow.columns != null) {
    decls.push(`grid-template-columns:repeat(${flow.columns},minmax(0,1fr))`);
  }
  const rules: string[] = [];
  if (decls.length) {
    rules.push(`${target}{${decls.map((d) => `${d}!important`).join(";")}}`);
  }
  if (flow.alignItems) {
    const children = [
      `${target} > *`,
      `${target} > .cms-grid-bridge > *`,
    ].join(",");
    rules.push(
      `${children}{align-self:${FLOW_ALIGN_CSS[flow.alignItems]}!important}`,
    );
  }
  return rules;
}

/**
 * Construit les règles CSS générées d'un bloc (retour `null` si aucune) :
 *  - règle de BASE (desktop) portant les valeurs des propriétés qui ont une
 *    surcharge tablette/mobile (elles quittent l'inline : une règle @media ne
 *    peut pas battre un style inline — même spécificité ensuite, l'ordre
 *    base → tablette → mobile fait la cascade desktop-first, sans !important) ;
 *  - `@media (max-width:1023px)` : surcharges tablette (s'appliquent aussi au
 *    mobile tant que non resurchargées = héritage Elementor) ;
 *  - `@media (max-width:767px)`  : surcharges mobile ;
 *  - visibilité par appareil (plages EXACTES : masquer tablette ne masque pas
 *    le mobile).
 *
 * `selector` = classe unique du wrapper (ex. `.cms-blk-r1`), générée par le
 * StyleWrapper (useId) et posée sur le même élément que le style inline.
 */
export function buildBlockCssRules(
  selector: string,
  style: BlockStyle,
): string | null {
  if (!blockStyleNeedsCssRules(style)) return null;
  const rules: string[] = [];
  const unit = style.spacing.unit;

  // Base desktop des propriétés déplacées hors inline.
  const base: string[] = [];
  boxDecls(base, "margin", style.spacing.margin, unit, (side) =>
    sideHasResponsive(style, "margin", side),
  );
  boxDecls(base, "padding", style.spacing.padding, unit, (side) =>
    sideHasResponsive(style, "padding", side),
  );
  if (scalarHasResponsive(style, "width") && style.layout.width != null) {
    base.push(`width:${style.layout.width}${style.layout.widthUnit}`);
  }
  if (
    scalarHasResponsive(style, "fontSize") &&
    style.design.text.fontSize != null
  ) {
    base.push(`font-size:${style.design.text.fontSize}px`);
  }
  if (
    scalarHasResponsive(style, "letterSpacing") &&
    style.design.text.letterSpacing != null
  ) {
    base.push(`letter-spacing:${style.design.text.letterSpacing}px`);
  }
  if (
    scalarHasResponsive(style, "lineHeight") &&
    style.design.text.lineHeight != null
  ) {
    base.push(`--cms-line-height:${style.design.text.lineHeight}`);
  }
  if (textAlignHasResponsive(style)) {
    base.push(`text-align:${style.design.text.align}`);
  }
  if (base.length) rules.push(`${selector}{${base.join(";")}}`);
  if (textAlignHasResponsive(style)) {
    // Tous les descendants suivent l'alignement du wrapper, y compris à travers
    // un parent qui porte sa propre classe `text-*` (Accroche, Citation, Texte
    // riche, Bannière, légendes). Classe doublée : bat les classes Tailwind
    // sans `!important`, donc l'alignement inline d'un paragraphe TipTap reste
    // prioritaire, comme sur desktop.
    rules.push(`${selector}${selector} *{text-align:inherit}`);
  }

  const devices: [ResponsiveStylePatch, number][] = [
    [style.responsive.tablet, CMS_STYLE_BREAKPOINTS.tabletMax],
    [style.responsive.mobile, CMS_STYLE_BREAKPOINTS.mobileMax],
  ];
  for (const [patch, maxWidth] of devices) {
    const decls = patchDecls(style, patch);
    const inner = [
      ...(decls.length ? [`${selector}{${decls.join(";")}}`] : []),
      ...flowRules(selector, patch.flow),
    ];
    if (inner.length) {
      rules.push(`@media (max-width:${maxWidth}px){${inner.join("")}}`);
    }
  }

  const v = style.visibility;
  if (v.hideDesktop) {
    rules.push(
      `@media (min-width:${CMS_STYLE_BREAKPOINTS.tabletMax + 1}px){${selector}{display:none}}`,
    );
  }
  if (v.hideTablet) {
    rules.push(
      `@media (min-width:${CMS_STYLE_BREAKPOINTS.mobileMax + 1}px) and (max-width:${CMS_STYLE_BREAKPOINTS.tabletMax}px){${selector}{display:none}}`,
    );
  }
  if (v.hideMobile) {
    rules.push(
      `@media (max-width:${CMS_STYLE_BREAKPOINTS.mobileMax}px){${selector}{display:none}}`,
    );
  }

  return rules.length ? rules.join("\n") : null;
}
